'use client';

import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, AlertCircle, Database, Shield, Building2 } from 'lucide-react';

export default function BootstrapPage() {
  const init = useMutation(api.bootstrap.init);
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBootstrap = async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await init();
      setResult(res);
      setStatus('success');
      // Auto-redirect after a short delay
      setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-lg space-y-4">
        {/* Main action card */}
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Account Setup</CardTitle>
            <CardDescription>
              You're signed in, but your account hasn't been set up in the app database yet.
              Click below to create your profile.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleBootstrap}
              disabled={status === 'loading' || status === 'success'}
              className="w-full"
              size="lg"
            >
              {status === 'loading' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {status === 'success' && <CheckCircle2 className="mr-2 h-4 w-4" />}
              {status === 'idle' && 'Set Up My Account'}
              {status === 'loading' && 'Setting up...'}
              {status === 'success' && 'Done — redirecting...'}
              {status === 'error' && 'Try Again'}
            </Button>

            {status === 'success' && result && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                <p className="font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  {result.status === 'already_exists' ? 'Account already exists' : 'Account created'}
                </p>
                {result.role !== undefined && (
                  <p className="mt-1 text-green-600">Role: {String(result.role)}</p>
                )}
              </div>
            )}

            {status === 'error' && error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                <p className="font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Setup failed
                </p>
                <p className="mt-1 text-red-600">{error}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Explanation card */}
        <Card className="shadow-md border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Why is this needed?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex gap-3">
              <Shield className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                <strong className="text-foreground">Authentication</strong> (Clerk) confirms your identity — it knows <em>who</em> you are.
              </p>
            </div>
            <div className="flex gap-3">
              <Database className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                <strong className="text-foreground">The database</strong> (Convex) stores your app data — roles, permissions, and records. It doesn't know about you until a record is created.
              </p>
            </div>
            <div className="flex gap-3">
              <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                <strong className="text-foreground">Bootstrap</strong> bridges the gap — it takes your authenticated identity and creates matching user, role, and organization records in the database.
              </p>
            </div>
            <p className="text-xs text-muted-foreground/70 pt-2 border-t">
              In production, this is typically handled automatically by a webhook when you first sign up. This page exists for initial setup and development.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
